"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { useIdentity } from "@/lib/api/session";
import { BonusModal } from "@/components/casino/BonusModal";
import { rollupProgressView, useBonuses, useRollups, type Bonus } from "@/lib/api/incentives";
import { messageFor } from "@/lib/api/messages";
import { PlatformError } from "@/lib/api/client";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { SectionEmpty, SectionError } from "@/components/ui/states";
import { RewardsHistory } from "@/components/account/RewardsHistory";
import { YourRewards } from "@/components/account/YourRewards";
import { formatDate, toPlainText, truncate } from "@/lib/format/html";
import { EASE } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { Chip, Panel, PanelHeader, ProgressBar } from "@/components/ui/primitives";

/* The rewards page's body: the platform's bonuses as cards, the signed-in
   player's own rewards, and their history. */

const TYPE_LABEL: Record<string, string> = {
  cashback: "Cashback",
  rakeback: "Rakeback",
  free_spin: "Free spins",
  free_money: "Bonus funds",
  bounty: "Bounty",
  tier_assignment: "Tier reward",
};

const chipsFor = (bonus: Bonus) =>
  [bonus.type ? (TYPE_LABEL[bonus.type] ?? bonus.type) : null, bonus.interval, bonus.isRecurring ? "Recurring" : null].filter(
    (value): value is string => Boolean(value),
  );

const runsFor = (bonus: Bonus) => {
  if (!bonus.startAt) {
    return null;
  }

  return bonus.endAt
    ? `${formatDate(bonus.startAt)} – ${formatDate(bonus.endAt)}`
    : `Running since ${formatDate(bonus.startAt)}`;
};

export function RewardsBlock() {
  const { isSignedIn } = useIdentity();
  const bonuses = useBonuses();
  const rollups = useRollups(isSignedIn);
  const rollupByBonusId = new Map((rollups.data ?? []).map((rollup) => [rollup.bonusId, rollup]));

  /* The card truncates; the modal is where the rest of the reward lives. Any
     rollup at all gets the bar — rollupProgressView says "No progress yet" for
     an empty one, which beats hiding the bar from the player it belongs to. */
  const [selected, setSelected] = useState<Bonus | null>(null);
  const selectedRollup = selected ? (rollupByBonusId.get(selected.id) ?? null) : null;
  const selectedProgress = isSignedIn && selectedRollup ? rollupProgressView(selectedRollup) : null;

  return (
    <>
      <Panel>
        <PanelHeader icon="gift" title="Available Rewards" />

        {bonuses.isPending && (
          <div className="grid gap-3 px-6 pb-6 lg:grid-cols-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-[132px] animate-pulse rounded-lg bg-elevation-2" />
            ))}
          </div>
        )}

        {bonuses.isError && (
          <SectionError title="Rewards didn’t load" body={messageFor(bonuses.error as PlatformError, "rewards")} />
        )}

        {bonuses.isSuccess && bonuses.data.length === 0 && <SectionEmpty icon="gift" title="No bonuses are running yet" />}

        {bonuses.isSuccess && bonuses.data.length > 0 && (
          <div className="grid gap-3 px-6 pb-6 lg:grid-cols-2">
            {bonuses.data.map((bonus, i) => {
              const rollup = rollupByBonusId.get(bonus.id) ?? null;
              const progress = isSignedIn && rollup?.wageringProgress ? rollupProgressView(rollup) : null;

              return (
                /* A button, not an article with a handler bolted on: the card
                   already lifted on hover as if it did something, and until it
                   opened this modal that was a promise the page didn't keep. */
                <motion.button
                  key={bonus.id}
                  type="button"
                  onClick={() => setSelected(bonus)}
                  aria-haspopup="dialog"
                  aria-label={`${bonus.name} — details`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, ease: EASE, delay: i * 0.07 }}
                  className="flex cursor-pointer flex-col justify-between gap-4 rounded-lg bg-elevation-2 p-4 text-left transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="flex gap-3">
                    <span className="relative grid h-[70px] w-[70px] shrink-0 place-items-center overflow-hidden rounded-md bg-brand/18 text-brand-bold">
                      {bonus.imageUrl || bonus.iconUrl ? (
                        <RemoteImage src={bonus.imageUrl ?? bonus.iconUrl ?? ""} width={70} height={70} />
                      ) : (
                        <Icon name="gift" size={20} />
                      )}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-[14px] font-bold leading-[1.6]">{bonus.name}</h3>
                      {bonus.description && (
                        <p className="mt-1 text-[12px] leading-[1.6] text-fg-subtle">
                          {truncate(toPlainText(bonus.description), 220)}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {chipsFor(bonus).map((c) => (
                          <Chip key={c} className="bg-elevation-3 capitalize">
                            {c}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </div>

                  {runsFor(bonus) && <p className="text-[12px] leading-[1.6] text-fg-subtle">{runsFor(bonus)}</p>}

                  {progress && <ProgressBar value={Math.round(progress.percent)} height={20} label={progress.label} />}
                </motion.button>
              );
            })}
          </div>
        )}
      </Panel>

      <YourRewards />

      {isSignedIn && <RewardsHistory />}

      <BonusModal
        bonus={selected}
        chips={selected ? chipsFor(selected) : []}
        runs={selected ? runsFor(selected) : null}
        progress={selectedProgress}
        signedIn={isSignedIn}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
